import { Component, Input, Output, EventEmitter, ViewChild, OnInit, AfterViewInit, ElementRef, OnDestroy, ChangeDetectionStrategy, inject } from '@angular/core'
import { DatePipe, AsyncPipe, DecimalPipe, NgTemplateOutlet } from '@angular/common'
import { IconNamesEnum, NgxBootstrapIconsModule } from 'ngx-bootstrap-icons'
import { BackEndService } from '../../../services/backend.service'
import * as SearchTypes from './filtering-search-types'
import { SavedSearchService } from 'src/app/services/saved-search.service'
import { debounce } from 'typescript-debounce-decorator'
import { map, Observable, Subscription, take } from 'rxjs'
import { ActivatedRoute, Router } from '@angular/router'
import { Store } from '@ngrx/store'
import { openClose, optionGroupTrigger } from '../../animations/open-close-animations'
import { selectSearchState } from 'src/app/state'
import { selectUserState } from 'src/app/state/user.selectors'
import { NgSelectModule } from '@ng-select/ng-select'
import { DateRangeComponent } from '../date-range/date-range.component'
import { FormsModule } from '@angular/forms'
import escapeStringRegexp from 'escape-string-regexp'
import dayjs from 'dayjs'
import { Dictionary } from '@ngrx/entity'
import { NgxSliderModule } from '@angular-slider/ngx-slider'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'

export * from './filtering-search-types'

@Component({
  selector: 'base-filtering-search',
  templateUrl: './filtering-search.component-base.html',
  styleUrls: ['./filtering-search.component-base.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  animations: [
    openClose,
    optionGroupTrigger
  ],
  imports: [
    NgxBootstrapIconsModule,
    FormsModule,
    DateRangeComponent,
    NgSelectModule,
    NgxSliderModule,
    AsyncPipe,
    DecimalPipe,
    DatePipe,
    NgTemplateOutlet,
    FormsModule,
  ]
})

export class FilteringSearchComponentBase implements OnInit, AfterViewInit, OnDestroy {
  protected store = inject<Store<any>>(Store)
  protected backendService = inject(BackEndService)
  protected savedSearchService = inject(SavedSearchService)
  protected router = inject(Router)
  protected route = inject(ActivatedRoute)
  private datePipe = inject(DatePipe)
  private modalService = inject(NgbModal)
  private searchService = inject(BackEndService)

  @Input('options') set setSearchOptions(search: SearchTypes.SearchOptionGroup[]) {

    this.providedTopLevelSearchOptionGroups = search
    this.topLevelSearchOptionGroups = SearchTypes.initSearchOptionGroup(search)

    for (let groupOptions of this.topLevelSearchOptionGroups) {
      for (let option of groupOptions.options) {
        if (option.selected) {
          this.selectedOptions.push(option)
        }

        if (option.lookupOnPaste) {
          this.lookupOnPasteOption = option
        }
      }
    }
  }
  private initialLeftPosition = 12

  @Input('area') set setType(area: string) {
    this.area = area
  }

  @Input('search') set setSearchParameters(search: SearchTypes.NamedSearchParameters) {
    this.search = search
  }
  @Input('selectedoptions') set setSelectedOption(parameters: any) {
    this.loadSearch({ parameters }, true)
  }

  @Input() public requiredFilters: Dictionary<any> = {}
  @Input('saveSearches') allowSearchesToBeSaved = true
  @Input('saveSearchesText') saveSearchesText = 'Save Filters'
  @Input('showFilter') showFilter = true
  @Output('currentSearch') searchOutput = new EventEmitter<SearchTypes.NamedSearchParameters>()

  @ViewChild('autocompleteoptions') autocompleteoptions: ElementRef = {} as ElementRef
  @ViewChild('input') input: ElementRef = {} as ElementRef
  @ViewChild('companyChooser') pasteChooser: any

  static DefaultFilterPlaceHolder = 'Type here to filter....'
  public iconNames = IconNamesEnum

  public area: string = ''

  public today = new Date()

  public providedTopLevelSearchOptionGroups: SearchTypes.SearchOptionGroup[] = []

  public topLevelSearchOptionGroups: SearchTypes.SearchOptionGroup[] = []

  public filterPlaceholder = FilteringSearchComponentBase.DefaultFilterPlaceHolder
  public selectedOptions: SearchTypes.SearchOption[] = []
  private lookupOnPasteOption: SearchTypes.SearchOption | undefined
  public selectedOptionActive = -1
  public autocompleteText = ''
  public searchRequest: any = null
  public leftPosition = this.initialLeftPosition
  public searchTimer: ReturnType<typeof setTimeout> | null = null

  public search: SearchTypes.NamedSearchParameters = {
  }

  public searchSummary: {
    total: number
    selected: number
  } = {
      total: 0,
      selected: 0
    }
  public searchResults = []

  // used to inject enumeration values into template
  public searchOptionType = SearchTypes.SearchOptionType

  public userQueryType: string = ''
  public searchNameToDelete: string = ''

  public pasteChooserLoading = true
  public pastedChooserHeader = ''
  public pastedChoices$: Observable<any> = new Observable<any>
  public pastedChoices: any[] = []

  public searchTypes = SearchTypes

  public paramsSubscription$: Subscription = new Subscription
  public routerState: any
  public userState$

  private lastAutocompleteText = ''

  constructor() {
    const currentNavigation = this.router.getCurrentNavigation()
    const previousNavigation = this.router.lastSuccessfulNavigation
    delete previousNavigation?.extras.state?.['articlesLoaded']
    this.routerState = currentNavigation?.extras.state ?? previousNavigation?.extras.state
    this.userState$ = this.store.select(selectUserState)
  }

  ngOnInit(): void {

    // the subscription is across all routes so this fires when we move from /xxxx/&q to /xxxxx/1&q
    this.paramsSubscription$ = this.route.queryParams.subscribe(params => {
      let parameters
      let articlesAlreadyLoaded = false
      if (this.routerState?.['articlesLoaded']) {
        // we are here becase we have come back from an article in mobile mode which means we have articles in the store
        this.store.select(selectSearchState).pipe(take(1)).subscribe(
          s => parameters = s.parameters
        )
        articlesAlreadyLoaded = true
        this.routerState = null
      } else if (this.routerState?.['parameters']) {
        parameters = this.routerState['parameters']
        this.routerState = null
      } else {
        parameters = Object.fromEntries(Object.entries(params).map(([key, value]) => {
          if (value.includes(':')) {
            let [id, title] = value.split(':')
            if (id === '0') {
              title = `contains "${title}"`
            }
            return [key, {
              type: 'multiautocomplete',
              value: [{
                id,
                title
              }]
            }]
          } else if (key === 'name' || key == 'content') {
            return [key, {
              type: 'stringinput',
              value
            }]
          } else if (key == 'docketid') {
            return [key, {
              type: 'searchnumbers',
              value
            }]
          } else if (key == 'docid') {
            return ['docketid', {
              type: 'searchnumbers',
              value
            }]
          } else {
            return [key, {
              type: 'checkboxes',
              value: value.split(',')
            }]
          }
        }))
      }
      if (Object.keys(parameters).length > 0) {
        this.loadSearch({ parameters }, true)
      } else {
        this.loadSearch(this.search)
      }
      if (!articlesAlreadyLoaded) {
        this.makeSearchRequest()
      }
    })
  }

  ngAfterViewInit() {
    const threshold = 0.2 // how much % of the element is in view
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.rootBounds !== null && (entry.boundingClientRect.left + entry.boundingClientRect.width) > entry.rootBounds.width) {
              this.leftPosition = this.initialLeftPosition - (entry.rootBounds.width - entry.boundingClientRect.left)
            } else {
              this.leftPosition = this.initialLeftPosition
            }
          }
        })
      },
      { threshold }
    )
    if (this.autocompleteoptions.nativeElement) {
      observer.observe(this.autocompleteoptions.nativeElement)
    }
  }

  ngOnDestroy() {
    this.paramsSubscription$.unsubscribe()
    this.resetSearch()
  }

  checkForVisibleIfPresent(option: SearchTypes.SearchOption): boolean {
    return ((option?.tableColumn?.visibleIfPresent ?? false) && this.searchResults.length > 0 && this.searchResults[0][option.id])
  }

  checkForVisibleIfOptionGroupFilterEngaged(option: SearchTypes.SearchOption, optionGroup: SearchTypes.SearchOptionGroup): boolean {
    return ((option?.tableColumn?.visibleIfOptionGroupFilterEngaged || false) && this.searchResults.length > 0 && optionGroup.options.some(o => o.selected))
  }

  makeSearchRequest(startAt: number = 1) {
    this.search = {
      ... this.search,
      area: this.area,
      window: {
        start: startAt,
        size: this.search?.window?.size ?? 20
      },
      parameters: {
        ...this.getSearchParameters(),
        ...this.requiredFilters
      }
    }
    this.searchOutput.emit(this.search)
  }

  loadContent($event: Event | KeyboardEvent | undefined = undefined) {
    if ($event && $event instanceof KeyboardEvent && $event.key.length > 1) {
      return
    }
    if (this.searchTimer) {
      clearTimeout(this.searchTimer)
    }
    this.searchTimer = setTimeout(() => {
      this.makeSearchRequest()
    }, 500)
  }

  createAutoCompleteOptions(autocompleteText: string, searchServiceParameter: SearchTypes.Dictionary<any>): SearchTypes.Dictionary<any> {
    let params = structuredClone(searchServiceParameter)
    for (let parameter of Object.values(params!['parameters'])) {
      (parameter as any).value = autocompleteText
    }
    return params
  }

  handlePaste(event: ClipboardEvent) {
    if (this.lookupOnPasteOption) {
      const paste = event.clipboardData?.getData("text/plain") ?? ''
      const lines = paste.split('\n').map(string => string.trim()).filter(str => str.length > 0)
      if (lines.length > 1) {
        event.preventDefault()
        this.input.nativeElement.blur()
        this.pasteChooserLoading = true
        this.pastedChooserHeader = "Matching  " + this.lookupOnPasteOption.name
        this.pastedChoices = []

        const self = this
        this.modalService.open(this.pasteChooser, {
          backdropClass: 'diffuse-backdrop',
          keyboard: false,
          backdrop: 'static',
          scrollable: true,
          size: 'lg',
          centered: true,
        }).result.then(
          (result) => {
            let chosenOption: any
            this.topLevelSearchOptionGroups.forEach(optionGroup => {
              optionGroup.options.forEach(option => {
                if (option.id === 'creditorName') {
                  chosenOption = option
                }
              })
            })
            if (chosenOption) {
              chosenOption.required = chosenOption.required ?? []
              for (const choice of this.pastedChoices) {
                if (choice.checked) {
                  if (choice.match === 'contains') {
                    chosenOption.required.push({
                      id: choice.id,
                      title: `Contains '${choice.id}'`
                    })
                  } else {
                    chosenOption.required.push({
                      id: -1,
                      title: choice.id
                    })
                  }
                }
              }
              if (!self.selectedOptions.find(option => option.id === chosenOption.id)) {
                chosenOption.active = true
                chosenOption.selected = true
                chosenOption.tableColumn.visible = true
                self.selectedOptions.push(chosenOption)
              }
              self.makeSearchRequest()
            }
          },
        )

        this.pastedChoices$ = this.searchService.search({
          area: 'data/creditors2/autocomplete',
          parameters: {
            creditorNames: {
              name: "creditorNames",
              type: "stringinput",
              format: "autocomplete",
              returndata: "brief",
              value: lines
            }
          }
        }, false).pipe(map((result: any) => {
          this.pasteChooserLoading = false
          this.pastedChoices = []
          for (const choice of result.articles) {
            let match = 'contains'
            if (choice.matches.length === 2) {
              match = 'exact'
            }
            this.pastedChoices.push({
              match,
              checked: true,
              id: choice.id
            })
          }
          return result.articles
        }))
      }
    }
  }

  setupToShowAutoCompleteOptions(autocompleteText: string) {
    if (this.lastAutocompleteText === autocompleteText) {
      return
    }
    this.lastAutocompleteText = autocompleteText
    this.leftPosition = this.initialLeftPosition
    if (this.selectedOptionActive >= 0) {
      this.selectedOptions[this.selectedOptionActive].active = false
      this.selectedOptionActive = -1
    }
    this.topLevelSearchOptionGroups.forEach(optionGroup => {
      optionGroup.visible = optionGroup.options.reduce((acc, option) => {
        if (option.lastResort && !option.selected) {
          option.visible = true
          if (SearchTypes.isSearchStringOption(option)) {
            option.searchString = autocompleteText
          } else if (SearchTypes.isSearchNumbersOption(option)) {
            option.numberRange = autocompleteText
          } else if (SearchTypes.isSearchMultiAutoCompleteOption(option)) {
            option.items = []

            if (option.searchTimer) {
              clearTimeout(option.searchTimer)
            }
            option.searchTimer = setTimeout(() => {
              if (this.autocompleteText.length > 2) {
                if (option.searchServiceParameter !== undefined) {
                  option.searching = true

                  this.backendService.search(this.createAutoCompleteOptions(autocompleteText, option.searchServiceParameter)).subscribe((resp) => {
                    if (resp.articles) {
                      option.items = resp.articles.map((item: any) => {
                        Object.defineProperties(item, {
                          filterby: {
                            value: '',
                            writable: true,
                            enumerable: true
                          }
                        })
                        return item
                      })
                      option.searching = false
                    }
                  })
                }
              }
            }, 300)
          }
        } else {
          const regex = RegExp(escapeStringRegexp(autocompleteText), 'i')
          option.visible = regex.test(option.name)
          if (!option.visible && SearchTypes.isSearchCheckboxOption(option)) {
            option.visible = option.checkboxes.some(checkbox => regex.test(checkbox.value + ' ' + checkbox.name))
          }
        }
        return acc || option.visible
      }, false) as boolean
    })
  }

  dependenciesMet(depends: SearchTypes.Dictionary<string[]>, overlap: boolean = true): boolean {
    let result = true
    if (depends) {
      for (const [key, requiredValues] of Object.entries(depends)) {
        const visibleOption = this.selectedOptions.find(element => element.id === key)
        if (visibleOption) {
          if (requiredValues.length > 0) {
            if (SearchTypes.isSearchCheckboxOption(visibleOption)) {
              const selectedValues = visibleOption.checkboxes.filter(c => c.checked).map(c => c.value)

              const intersection = requiredValues.filter(value => selectedValues.includes(value))
              if (overlap) {
                result = intersection.length > 0
              } else {
                result = intersection.length === selectedValues.length
              }
            }
          }
        } else {
          result = false
        }
      }
    }
    return result
  }

  checkboxDependenciesMet(option: SearchTypes.CheckboxOption, matchingString = ''): boolean {
    let result = this.dependenciesMet(option.depends!)
    if (!result) {
      option.checked = option.checkedDefault
    }
    if (result && matchingString !== '') {
      // escape regex
      result = RegExp(matchingString.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i').test(option.name)
    }
    return result
  }

  allDependenciesMet(option: SearchTypes.SearchOption | SearchTypes.SearchOptionGroup): boolean {
    let anyDependenciesMet = true
    if (option.depends) {
      anyDependenciesMet = this.dependenciesMet(option.depends!)
    }
    if (anyDependenciesMet && option.dependsMust!) {
      anyDependenciesMet = anyDependenciesMet && this.dependenciesMet(option.dependsMust!, false)
    }
    return anyDependenciesMet
  }
  allDependenciesMetForSearchOptionGroup(optionGroup: SearchTypes.SearchOptionGroup): boolean {
    const anyDependenciesMet = this.allDependenciesMet(optionGroup)
    if (!anyDependenciesMet) {
      // remove any visible options from this optionGroup
      let remainingOptions: SearchTypes.SearchOption[] = []
      this.selectedOptions.forEach(option => {
        if (optionGroup.options.find((opt) => opt.id === option.id)) {
          this.resetOption(option)
        } else {
          remainingOptions.push(option)
        }
      })
      this.selectedOptions = remainingOptions
    }
    return anyDependenciesMet
  }

  optionGroupHasSomeVisibleElements(optionGroup: SearchTypes.SearchOptionGroup): boolean {
    return optionGroup.options.some(option => !option.selected && this.allDependenciesMet(option))
  }

  updateSelectedOptionDependencies() {
    let remainingOptions: SearchTypes.SearchOption[] = []
    this.selectedOptions.forEach(option => {
      if (this.allDependenciesMet(option)) {
        remainingOptions.push(option)
      } else {
        this.resetOption(option)
      }
    })
    this.selectedOptions = remainingOptions
  }

  topLevelSearchOptionSelect(option: SearchTypes.SearchOption, checkbox: SearchTypes.CheckboxOption | null = null, autoCompleteOption: any = null, forceRefreshAllowed = true): void {
    option.selected = true
    option.visible = false
    option.active = true

    let forceRefresh = false

    if (this.selectedOptionActive >= 0) {
      this.selectedOptions[this.selectedOptionActive].active = false
    }

    this.selectedOptionActive = this.selectedOptions.push(option) - 1

    if (SearchTypes.isSearchStringOption(option)) {
      forceRefresh = option.searchString.trim().length > 0
    } else if (SearchTypes.isSearchNumbersOption(option)) {
      forceRefresh = option.numberRange.trim().length > 0
    } else if (SearchTypes.isSearchCheckboxOption(option)) {
      option.checkboxes?.forEach(box => {
        if (box.checkedDefault) {
          box.checked = checkbox === null
        } else {
          box.checked = false
        }
      })
      if (checkbox !== null) {
        checkbox.checked = true
        option.active = false
      }
      option.numberChecked = option.checkboxes.filter(c => c.checked).length
      forceRefresh = forceRefresh || (option.checkboxes.length > 1 && option.numberChecked === 1)
    } else if (SearchTypes.isSearchMultiAutoCompleteOption(option)) {
      if (autoCompleteOption !== null) {
        autoCompleteOption.filterby = ''
        option.required = [autoCompleteOption]
        forceRefresh = true
      }
    } else if (SearchTypes.isSearchShowSearchOptionGroup(option)) {
      option.active = false
      this.selectedOptionActive = -1
      this.input.nativeElement.focus()
    } else if (SearchTypes.isSwitchSearchOptionGroup(option)) {
      option.active = false
      this.selectedOptionActive = -1
      forceRefresh = true
    }

    option.tableColumn!.visible = true

    this.autocompleteText = ''

    if (forceRefresh && forceRefreshAllowed) {
      this.makeSearchRequest()
    }
  }

  toggleActiveOption(optionToToggle: number) {
    let option = this.selectedOptions[optionToToggle]
    if (option.type !== SearchTypes.SearchOptionType.switchoptiongroup) {
      option.active = !option.active
      if (option.active) {
        this.selectedOptionActive = optionToToggle
      } else {
        this.selectedOptionActive = -1
      }
    }
  }

  acceptText() {
    const nativeElement: any = this.input.nativeElement
    nativeElement.blur()
    let visibleOptions: SearchTypes.SearchOption[] = []
    this.topLevelSearchOptionGroups.forEach(optionGroup => {
      optionGroup.options.forEach(option => {
        if (option.visible) {
          visibleOptions.push(option)
        }
      })
    })
    if (visibleOptions.length == 1) {
      this.topLevelSearchOptionSelect(visibleOptions[0])
    }
  }
  resetOption(option: SearchTypes.SearchOption) {
    option.selected = false
    option.visible = true
    option.active = false
    if (SearchTypes.isSearchNumberRangeOption(option)) {
      option.minValue = option.options?.floor ?? 1
      option.maxValue = option.options.ceil ?? 100
      option.checkboxes?.forEach((checkbox => checkbox.checked = true))
      if (option.orPercent) {
        option.orPercentOptions = {
          minValue: 0,
          maxValue: 100,
          showPercent: false,
        }
      }
    } else if (SearchTypes.isSearchCheckboxOption(option)) {
      option.checkboxes?.forEach((checkbox => checkbox.checked = checkbox.checkedDefault))
    } else if (SearchTypes.isSearchDateRangeOption(option)) {
      option.minDate = new Date()
      option.maxDate = new Date()
      option.relativeRange = ''
    } else if (SearchTypes.isSearchStringOption(option)) {
      option.searchString = ''
    } else if (SearchTypes.isSearchMultiAutoCompleteOption(option)) {
      option.required = []
      // option.filterby = null
      option.items = []
    }
  }

  topLevelSearchOptionDeSelect(optionToUnselect: number): void {
    let option = this.selectedOptions[optionToUnselect]
    this.resetOption(option)
    this.selectedOptions.splice(optionToUnselect, 1)
    this.selectedOptionActive = -1

    if (this.selectedOptions.length === 0) {
      this.filterPlaceholder = FilteringSearchComponentBase.DefaultFilterPlaceHolder
    }
    this.updateSelectedOptionDependencies()
    this.loadContent()
  }

  handleCheckboxGroup(option: SearchTypes.SearchCheckboxOption, searchParameters: SearchTypes.Dictionary<any>): SearchTypes.Dictionary<any> {
    let checkedBoxes: string[] = option.checkboxes.reduce((acc, checkbox) => {
      if (checkbox.checked) {
        acc.push(checkbox.value!)
        if (checkbox.conditionalOptions) {
          searchParameters = this.handleCheckboxGroup(checkbox.conditionalOptions, searchParameters)
        }
      }
      return acc
    }, [] as string[])

    if (option.alwaysSend || (checkedBoxes.length !== option.checkboxes.length) || (checkedBoxes.length === 1 && option.checkboxes.length === 1)) {
      searchParameters[option.id] = {
        name: option.name,
        type: 'checkboxes',
        value: checkedBoxes
      }
    }

    if (option.tableColumn) {
      option.tableColumn.visible = checkedBoxes.length > 1
    }
    return searchParameters
  }

  getSearchParameters(): SearchTypes.Dictionary<any> {
    let searchParameters: SearchTypes.Dictionary<any> = {}
    this.topLevelSearchOptionGroups.forEach(optionGroup => {
      optionGroup.options.forEach(option => {
        if (option.selected) {
          switch (option.type) {
            case SearchTypes.SearchOptionType.checkboxes:
              if (SearchTypes.isSearchCheckboxOption(option)) {
                searchParameters = this.handleCheckboxGroup(option, searchParameters)
              }
              break
            case SearchTypes.SearchOptionType.daterange:
              if (SearchTypes.isSearchDateRangeOption(option)) {
                searchParameters[option.id] = {
                  name: option.name,
                  type: 'daterange',
                  value: [
                    dayjs(option.minDate).format('YYYY-MM-DD'),
                    dayjs(option.maxDate).format('YYYY-MM-DD'),
                    option.relativeRange
                  ]
                }
              }
              break
            case SearchTypes.SearchOptionType.numberrange:
              if (SearchTypes.isSearchNumberRangeOption(option)) {
                let result: any = {
                  name: option.name,
                  type: 'numberrange',
                  unit: option.prefix,
                  value: [option.minValue, option.maxValue]
                }
                if (option.orPercent && option.orPercentOptions!.showPercent) {
                  result.value = [option.orPercentOptions!.minValue, option.orPercentOptions!.maxValue] as any[]
                  result.unit = '%'
                }
                if (option.checkboxes && option.checkboxes?.length > 0) {
                  result.value = option.checkboxes.reduce((acc, checkbox) => {
                    if (checkbox.checked) {
                      acc.push(checkbox.value)
                    }
                    return acc
                  }, result.value) as any[]
                }
                searchParameters[option.id] = result
              }
              break
            case SearchTypes.SearchOptionType.multiautocomplete:
              if (SearchTypes.isSearchMultiAutoCompleteOption(option)) {
                searchParameters[option.id] = {
                  name: option.name,
                  type: 'multiautocomplete',
                  value: option.required?.map((value: any) => ({
                    ...value,
                    filterby: option.itemfilters?.[value.id] ?? '',
                  })),
                  filterby: option.filterby,
                }
              }
              break
            case SearchTypes.SearchOptionType.stringinput:
              const searchString = (option as SearchTypes.SearchStringOption).searchString
              if (searchString.length > 0) {
                searchParameters[option.id] = {
                  name: option.name,
                  type: 'stringinput',
                  value: (option as SearchTypes.SearchStringOption).searchString,
                  match: (option as SearchTypes.SearchStringOption).matchingOption,
                  filter: (option as SearchTypes.SearchStringOption).checkboxes?.reduce((acc, checkbox) => {
                    if (checkbox.checked) {
                      acc.push(checkbox.value!)
                    }
                    return acc
                  }, [] as string[]),
                }
              }
              break
            case SearchTypes.SearchOptionType.switchoptiongroup:
              searchParameters[option.id] = {
                name: option.name,
                type: 'switch',
                value: (option as SearchTypes.SwitchSearchOptionGroup).value,
              }
              break
            case SearchTypes.SearchOptionType.select:
              searchParameters[option.id] = {
                name: option.name,
                type: 'select',
                value: (option as SearchTypes.SearchSelectOption).selectedValue,
              }
              break
            case SearchTypes.SearchOptionType.searchnumbers:
              const numberRange = (option as SearchTypes.SearchNumbersOption).numberRange
              if (numberRange.length > 0) {
                searchParameters[option.id] = {
                  name: option.name,
                  type: 'searchnumbers',
                  value: (option as SearchTypes.SearchNumbersOption).numberRange,
                }
              }
              break
          }
        }
      })
    })
    return searchParameters
  }

  resetSearch() {
    for (let i = 0; i < this.selectedOptions.length; i++) {
      let option = this.selectedOptions[i]
      this.resetOption(option)
    }
    this.selectedOptions = []
    this.selectedOptionActive = -1
    this.filterPlaceholder = FilteringSearchComponentBase.DefaultFilterPlaceHolder
  }

  clearSearch() {
    this.router.navigate([this.area], {
      onSameUrlNavigation: 'reload'
    })
    this.resetSearch()
    this.makeSearchRequest()
  }

  numberOfColumns(length: number | undefined): number {
    let result = 1
    if (length) {
      result = Math.ceil(length / 20)
    }
    return result
  }


  getFirstChecked(checkboxes: SearchTypes.CheckboxOption[]): string {
    return checkboxes.find(c => c.checked)?.name ?? ''
  }

  checkforActivateDependents(checkbox: SearchTypes.CheckboxOption, option: SearchTypes.SearchCheckboxOption | SearchTypes.SearchNumberRangeOption | SearchTypes.SearchStringOption) {
    if (option.numberChecked === 1 && checkbox.activateDependents && checkbox.activateDependents.length > 0) {
      for (let groupOptions of this.topLevelSearchOptionGroups) {
        for (let option of groupOptions.options) {
          if (checkbox.activateDependents!.includes(option.id)) {
            option.selected = true
            this.selectedOptions.push(option)
          }
        }
      }
    }
  }

  toggleCheckBox(checkbox: SearchTypes.CheckboxOption, option: SearchTypes.SearchCheckboxOption | SearchTypes.SearchNumberRangeOption | SearchTypes.SearchStringOption) {
    checkbox.checked = !checkbox.checked
    option.numberChecked = option.checkboxes!.filter(c => c.checked).length

    this.checkforActivateDependents(checkbox, option)
    this.updateSelectedOptionDependencies()
    this.loadContent()
  }

  changeCheckboxes(checkbox: SearchTypes.CheckboxOption, option: SearchTypes.SearchCheckboxOption | SearchTypes.SearchStringOption) {
    if (checkbox.checked && option.numberChecked === 1) {
      option.checkboxes!.forEach(c => {
        c.checked = true
      })
    } else {
      option.checkboxes!.forEach(c => {
        c.checked = false
      })
      checkbox.checked = true
    }
    option.numberChecked = option.checkboxes!.filter(c => c.checked).length

    this.checkforActivateDependents(checkbox, option)
    this.updateSelectedOptionDependencies()
    this.loadContent()
  }

  updateCheckboxeOnlyAll(checkbox: SearchTypes.CheckboxOption, option: SearchTypes.SearchCheckboxOption) {
    checkbox.onlyAll = 'Only'
    if (checkbox.checked && option.numberChecked === 1) {
      checkbox.onlyAll = 'All'
    }
  }

  setDateRange(option: SearchTypes.SearchDateRangeOption, dateRange: string) {
    option.relativeRange = dateRange
    option.maxDate = new Date()
    let date = new Date()
    switch (dateRange) {
      case '1d':
        date.setDate(date.getDate() - 1)
        option.minDate = date
        break
      case 'yd':
        date.setDate(date.getDate() - 1)
        option.minDate = date
        option.maxDate = date
        break
      case 'wd':
        let day
        do {
          date.setDate(date.getDate() - 1)
          day = date.getDay()
        } while (day === 0 || day === 6)
        option.minDate = date
        option.maxDate = date
        break
      case '1w':
        date.setDate(date.getDate() - 7)
        option.minDate = date
        break
      case '1m':
        date.setMonth(date.getMonth() - 1)
        option.minDate = date
        break
      case '1y':
        date.setFullYear(date.getFullYear() - 1)
        option.minDate = date
        break
      case 'ytd':
        date.setMonth(0)
        date.setDate(1)
        option.minDate = date
        break
    }
    this.loadContent()
  }

  friendlyRange(minDate: Date | string, maxDate: Date | string): string {
    if (typeof minDate === 'string') {
      minDate = new Date(minDate)
      maxDate = new Date(maxDate)
    }
    const daysDiff = Math.round(((maxDate as Date).getTime() - (minDate as Date).getTime()) / (3600 * 24 * 1000))
    if (daysDiff > 360) {
      return Math.round(daysDiff / 365) + 'y'
    } else if (daysDiff > 28) {
      return Math.round(daysDiff / 28) + 'm'
    } else if (daysDiff > 6) {
      return Math.round(daysDiff / 7) + 'w'
    } else {
      return daysDiff + 'd'
    }
  }

  loadContentAfterDateChange(d: Date, option: SearchTypes.SearchDateRangeOption, dateField: Date) {
    option.relativeRange = ''
    // dateField = d
    this.loadContent()
  }

  multiAutocompleteChange(event: Event, option: SearchTypes.SearchMultiAutoCompleteOption) {
    option.items = []
    this.loadContent()
  }

  @debounce(750)
  multiAutocompleteSearch(event: any, option: SearchTypes.SearchMultiAutoCompleteOption) {
    if (event && event.term) {
      const search = event.term
      if (search.length >= 2) {
        option.searching = true
        this.backendService.search(this.createAutoCompleteOptions(search, option.searchServiceParameter!)).subscribe((resp) => {
          if (resp.articles) {
            option.items = resp.articles.map((item: any) => {
              item.filterby = ''
              return item
            })
          }
          option.searching = false
        })
      } else {
        option.items = []
      }
    }
  }

  multiAutocompleteNgSelectSearchFn(term: string, item: any): boolean {
    return true
  }

  multiAutocompleteRemove(event: Event, option: SearchTypes.SearchMultiAutoCompleteOption) {
    this.loadContent()
  }

  multiAutocompleteClear(option: SearchTypes.SearchMultiAutoCompleteOption) {
    this.loadContent()
  }

  loadSearch(search: SearchTypes.NamedSearchParameters, withReset = false) {

    if (withReset) {
      this.resetSearch()
      this.search.parameters = {}
    }


    for (let groupOptions of this.topLevelSearchOptionGroups) {
      for (let option of groupOptions.options) {
        let parameter = search.parameters?.[option.id]
        if (parameter !== undefined) {
          this.search = {
            ...this.search,
            parameters: {
              ...this.search.parameters,
              [option.id]: search.parameters![option.id]
            }
          }
          if (!option.selected) {
            this.topLevelSearchOptionSelect(option, null, null, false)
            option.active = false
            option.tableColumn!.visible = true
            switch (option.type) {
              case SearchTypes.SearchOptionType.checkboxes:
                if (SearchTypes.isSearchCheckboxOption(option)) {
                  option.checkboxes.forEach((checkbox) => {
                    checkbox.checked = parameter.value.indexOf(checkbox.value) >= 0

                    if (checkbox.conditionalOptions && search.parameters?.[checkbox.conditionalOptions.id]) {
                      checkbox.conditionalOptions.checkboxes.forEach((subcheckbox) => {
                        subcheckbox.checked = search.parameters?.[checkbox.conditionalOptions!.id].value.indexOf(subcheckbox.value) >= 0
                        return subcheckbox
                      })
                      checkbox.conditionalOptions.numberChecked = checkbox.conditionalOptions.checkboxes.filter(c => c.checked).length
                    }

                    return checkbox
                  })
                  option.numberChecked = option.checkboxes.filter(c => c.checked).length

                }
                break
              case SearchTypes.SearchOptionType.daterange:
                if (SearchTypes.isSearchDateRangeOption(option)) {
                  const [minDate, maxDate] = parameter.value
                  option.minDate = new Date(minDate)
                  option.maxDate = new Date(maxDate)
                }
                break
              case SearchTypes.SearchOptionType.numberrange:
                if (SearchTypes.isSearchNumberRangeOption(option)) {
                  [option.minValue, option.maxValue] = parameter.value
                }
                break
              case SearchTypes.SearchOptionType.searchnumbers:
                if (SearchTypes.isSearchNumbersOption(option)) {
                  option.numberRange = parameter.value
                }
                break
              case SearchTypes.SearchOptionType.stringinput:
                if (SearchTypes.isSearchStringOption(option)) {
                  option.searchString = parameter.value
                  if (option.checkboxes && option.checkboxes.length > 0) {
                    option.checkboxes.forEach((checkbox) => {
                      // not sure about this
                      // checkbox.checked = parameter.value.indexOf(checkbox.value) >= 0
                      checkbox.checked = true
                      return checkbox
                    })
                    option.numberChecked = option.checkboxes.filter(c => c.checked).length
                  }
                }
                break
              case SearchTypes.SearchOptionType.select:
                if (SearchTypes.isSearchSelectOption(option)) {
                  option.selectedValue = parameter.value
                }
                break
              case SearchTypes.SearchOptionType.multiautocomplete:
                if (SearchTypes.isSearchMultiAutoCompleteOption(option)) {
                  option.required = parameter.value
                }
                break
            }
          }
        }
      }
    }
    this.selectedOptionActive = -1
    if (search.columns) {
      this.topLevelSearchOptionGroups.forEach(optionGroup => {
        optionGroup.options.forEach(option => {
          option.tableColumn = {
            ...option.tableColumn,
            visible: (search.columns!.indexOf(option.id) !== -1)
          }
        })
      })
    }

    if (search.order) {
      this.search.order = { ...search.order }
    }
  }

  protected createSearchName(): string {
    let searchNameParts: string[] = []
    for (const option of Object.values(this.search.parameters as any) as any[]) {
      let clause = option.name
      if (option.type == 'checkboxes') {
        const checkboxes = (this.selectedOptions.find(o => o.name === option.name) as SearchTypes.SearchCheckboxOption)?.checkboxes ?? []
        const valueNames = option.value.map((value: string) => checkboxes.find(c => c.value == value)?.name)
        clause = clause + ': ' + valueNames.join(',')
      } else if (option.type == 'numberrange') {
        clause = clause + ': ' + option.value[0] + ' - ' + option.value[1]
      } else if (option.type == 'daterange') {
        if (option.value[2] === '') {
          clause = clause + ': (' + this.friendlyRange(option.value[0], option.value[1]) + ') ' + this.datePipe.transform(option.value[0]) + ' - ' + this.datePipe.transform(option.value[1])
        } else {
          switch (option.value[2]) {
            case '1d':
              clause = clause + ': last day'
              break
            case '1w':
              clause = clause + ': last week'
              break
            case '1m':
              clause = clause + ': last month'
              break
            case '1y':
              clause = clause + ': last year'
              break
          }
        }
      } else if (option.type == 'stringinput') {
        clause = clause + ': ' + option.value
      }
      searchNameParts.push(clause)
    }
    return searchNameParts.join(', ') ?? ''
  }

  saveSearch() {
    let parameters: any
    this.store.select(selectSearchState).pipe(take(1)).subscribe(
      s => parameters = s
    )
    this.savedSearchService.saveSearch({
      area: this.area.split('/')[0],
      parameters: {
        ...this.search,
        ...parameters,
      },
      type: 'search',
      name: this.createSearchName(),
    })
  }

  optionHasCheckboxes(option: SearchTypes.SearchOption) {
    return SearchTypes.isSearchCheckboxOption(option) || (SearchTypes.isSearchStringOption(option) && option.checkboxes && option.checkboxes!.length > 0)
  }

  showCheckboxConditionalOptions(option: SearchTypes.SearchCheckboxOption, currentCheckbox: SearchTypes.CheckboxOption): boolean {
    let result = false
    if (currentCheckbox.checked && currentCheckbox.conditionalOptions) {
      if (currentCheckbox.conditionalOptions.showWhenOnlyChecked) {
        result = option.checkboxes.filter(c => c.checked).length === 1
      } else {
        result = true
      }
    } else {
      result = false
    }
    if (result) {
      if (!this.selectedOptions.find(o => o.id === currentCheckbox.conditionalOptions?.id)) {
        this.selectedOptions.push(currentCheckbox.conditionalOptions!)
      }
    }
    return result
  }
}